import { ENTITY_TYPE } from './entityType';

export type SearchWorkEntityType = 'all' | 'epic' | 'goal' | 'project';

/**
 * Single source of truth for tab order/values — shared by the tab strip UI and the search hook /
 * per-tab query logic, so they can't diverge.
 */
export const SEARCH_WORK_TABS: SearchWorkEntityType[] = [
	ENTITY_TYPE.ALL,
	ENTITY_TYPE.PROJECT,
	ENTITY_TYPE.EPIC,
	ENTITY_TYPE.GOAL,
];

/**
 * A single recent work item, normalised for display in the Your Work search panel.
 *
 * This is the canonical shape the recents pipeline produces and the list UI consumes, decoupling
 * the UI from the raw Activity Platform response. The full pool is fetched once and filtered per
 * tab on the client by {@link RecentWorkItem.category} (e.g. the Epics tab keeps items whose
 * category is `epic`).
 */
export interface RecentWorkItem {
	/** The item's object id (ARI). */
	ari: string;
	/** Display title. For Jira this is `KEY: summary`; for Atlas it's the project/goal name. */
	name: string;
	/** Absolute URL to the item. Also used as the dedupe key. */
	url: string;
	/**
	 * Which tab this item belongs to, used for client-side per-tab filtering.
	 *
	 * Note: only `epic`, `project`, and `goal` are ever assigned — non-epic Jira issues are dropped
	 * during mapping since the panel only surfaces epics/projects/goals. `all` is never stored on an
	 * item (it's a filter that matches everything).
	 */
	category: Exclude<SearchWorkEntityType, 'all'>;
	/** Human-readable source product name for display, e.g. `Jira` or `Atlas`. */
	product: string;
	/** Icon URL, if available (Jira issue type avatar or Atlas icon). */
	iconUrl?: string;
	/** Secondary label — Jira project name, or the Atlas project/goal key. */
	container?: string;
	/** When the user last viewed the item, if available. */
	lastViewedDate?: Date;
}
